
/**
 * Define the `cog-booking` custom element for live booking status updates.
 *
 * The component automatically updates the visibility of child elements based on the current booking status.
 * You can include any markup inside the component and use the `data-booking-status` attribute to define which
 * elements should be shown for each booking status.
 *
 * Attributes:
 * - `status` (string): The current status of the booking. This can be any string but these are  of the following:
 *  - `coming-soon`: The booking is not yet available.
 *  - `priority-booking`: The booking is available to priority customers.
 *  - `general-booking`: The booking is available to the general public.
 *  - `last-few`: The booking is available to the general public, but there are only a few tickets left.
 *  - `sold-out`: The booking is sold out.
 *
 * - `priority-onsale-time` (int): The unix timestamp when the booking is available to priority customers.
 * - `general-onsale-time` (int): The unix timestamp when the booking is available to the general public.
 * - `last-few` (boolean): Whether the booking is in the last few tickets.
 * - `debug` (boolean): Whether to show debug information.
 * - `static` (boolean): Whether the component should update automatically.
 *
 * Times should be passed in as unix timestamps - a twig and PHP helper are registered in CogPress to help with this.
 * [`datetime_to_unix` and `\Core\Helpers::convertDateTimeToUnix()`]
 *
 * @class CogBooking
 * @extends HTMLElement
 */
class CogBooking extends HTMLElement {
    constructor() {
        super();
    }

    static get observedAttributes() {
        return ["status"];
    }

    get status() {
        return this.getAttribute("status") || "coming-soon";
    }

    connectedCallback() {
        // Define properties

        this.manageDisplay = true; // Whether the component should manage the display property of the elements
        this.lastFew = this.hasAttribute("last-few");
        this.debug = this.hasAttribute("debug");
        this.static = this.hasAttribute("static");

        // if debug is true append debug div to the component
        if (this.debug) {
            const debug = document.createElement("div");
            debug.classList.add("cog-booking__debug");
            this.append(debug);
        }

        if (this.hasAttribute("priority-onsale-time")) {
            this.priorityOnSaleTime = parseInt(
                this.getAttribute("priority-onsale-time"),
                10,
            );
        }

        if (this.hasAttribute("general-onsale-time")) {
            this.generalOnSaleTime = parseInt(
                this.getAttribute("general-onsale-time"),
                10,
            );

            // For testing: Set the general onsale time to x seconds from now
            // this.generalOnSaleTime = Math.floor(Date.now() / 1000) + 5;
        }

        this.render();

        if (!this.static) {
            this.updateInterval = setInterval(() => this.render(), 1000);
        }
    }

    disconnectedCallback() {
        clearInterval(this.updateInterval);
    }

    render() {
        const currentTimestamp = Math.floor(Date.now() / 1000);

        let status = this.status;

        if (!this.static) {
            if (
                this.priorityOnSaleTime &&
                currentTimestamp >= this.priorityOnSaleTime &&
                currentTimestamp <= this.generalOnSaleTime
            ) {
                status = "priority-booking";
            } else if (
                this.generalOnSaleTime &&
                currentTimestamp >= this.generalOnSaleTime
            ) {
                status = "general-booking";

                if (this.lastFew) {
                    status = "last-few";
                }
            } else if (!this.generalOnSaleTime) {
                status = "general-booking";
            }

            if (status !== this.status) {
                this.setAttribute("status", status);
            }
        }

        // Show only the elements that match the current state
        this.querySelectorAll(`[data-booking-status]`).forEach((element) => {
            const bookingStatuses = element.dataset.bookingStatus.split(" ");

            if (bookingStatuses.includes(this.status)) {
                element.dataset.bookingVisible = "true";

                element.removeAttribute("inert");

                if (this.manageDisplay) {
                    element.style.display = "";
                }
            } else {
                element.dataset.bookingVisible = "false";

                // Ensure that the element is not focusable even if its not removed from the document flow
                element.setAttribute("inert", "true");

                if (this.manageDisplay) {
                    element.style.display = "none";
                }
            }
        });

        // Debug info
        if (this.debug) {
            const debugString = `<code>Current Time: <b>${new Date(
                currentTimestamp * 1000,
            ).toUTCString()}</b><br>
                Priority onsale: <b>${new Date(
                    this.priorityOnSaleTime * 1000,
                ).toUTCString()}</b><br>
                General onsale: <b>${new Date(
                    this.generalOnSaleTime * 1000,
                ).toUTCString()}</b><br>
                Button State: <b>${this.status}</b><br></code>`;

            this.querySelector(".cog-booking__debug").innerHTML = debugString;
        }
    }
}

customElements.define("cog-booking", CogBooking);
